<template>
    <span>
        <main v-if="page && page.nextPageLabel">
            <form class="form" @submit.prevent="nextFormStep">
                <ShopInfo/>
                <hr class="divider"/>
                <component
                    v-for="(question, key) in page.questions"
                    :key="`${key}_${page.id}`"
                    :value="question.answer"
                    v-bind:is="question.type"
                    v-bind="question.props"
                    :optionalLabel="$l('general_input__indicators__optional', 'Optional')"
                    @input="formfieldInput($event, {
                        key,
                        pageId: page.id,
                    })"
                />
                <Checkbox
                    v-if="showAnonymousCheckbox"
                    v-model="showName"
                    :label=" $l('review_form__show_name', 'Show name with Review')"
                />
                <div
                    class="form__submit_holder"
                    :class="{
                        'form__submit_holder--hidden':
                            isSubmitting && nextFormStepUrl.includes('success'),
                    }"
                >
                    <Button
                        @click.native="submit(!showName)"
                        class="submit submit--normal"
                    >{{ page.nextPageLabel }}</Button>
                </div>
            </form>
        </main>
    </span>
</template>

<script>
import {
    TextInput,
    SwitchInput,
    Checkbox,
    AmountOfUnitPicker,
    DropdownQuestion,
    RadioQuestion,
    SegmentedControlQuestion,
    NpsQuestion,
    CesQuestion,
    StarsQuestion,
    Button,
    FormMixin,
    TextInputQuestion,
    GenderInput,
    MultipleChoiceListQuestion,
} from 'feedback-company-vue-components';

import router from '@/router';
import Header from '@/components/molecules/Header.vue';
import ShopInfo from '@/components/molecules/ShopInfo.vue';
import ChoiceBlock from '@/components/molecules/ChoiceBlock.vue';

export default {
    name: 'ReviewForm',
    mixins: [FormMixin],
    components: {
        Header,
        ShopInfo,
        TextInput,
        SwitchInput,
        Checkbox,
        AmountOfUnitPicker,
        DropdownQuestion,
        RadioQuestion,
        SegmentedControlQuestion,
        NpsQuestion,
        CesQuestion,
        StarsQuestion,
        Button,
        TextInputQuestion,
        GenderInput,
        ChoiceBlock,
        MultipleChoiceListQuestion,
    },
    data() {
        return {
            showName: true,
        };
    },
    computed: {
        showAnonymousCheckbox() {
            return (
                this.nextFormStepUrl.includes('success')
                && this.$store.state.shop.allow_anonymous_submit === true
            );
        },
        isSubmitting() {
            return this.$store.state.loadStates.submit;
        },
        page() {
            let page = this.$store.state.pages[0];
            if (this.$route.params.questionCategory) {
                page = this.$store.getters.getPageById(this.$route.params.questionCategory);
            }
            return page;
        },
        nextFormStepUrl() {
            const currentIndex = this.page.index;
            const nextFormStep = this.$store.state.pages[currentIndex + 1];
            let nextFormStepUrl = `/${this.$route.params.shop}/success`;
            if (nextFormStep !== undefined) {
                nextFormStepUrl = `/${this.$route.params.shop}/${nextFormStep.id}`;
            }
            return nextFormStepUrl;
        },
    },
    watch: {
        showName(showName) {
            const nameIndex = this.page.questions.findIndex(
                question => question.id === 'fullname',
            );
            this.$store.commit('setRequired', {
                pageIndex: this.page.index,
                questionIndex: nameIndex,
                required: showName,
            });
        },
    },
    methods: {
        submit(anonymously) {
            this.$store.commit('submitAnonymous', anonymously);
            window.requestAnimationFrame(() => {
                this.nextFormStep();
            });
        },
        formfieldInput(answer, questionIndexData) {
            this.$store.commit('setAnswer', {
                answer,
                pageIndex: this.page.index,
                questionIndex: questionIndexData.key,
            });
        },
        nextFormStep() {
            this.showWhichInputsAreInvalid();
            if (!this.isValid) return false;
            if (this.nextFormStepUrl.includes('success')) {
                this.$store.dispatch('postReview');
            } else {
                router.push(this.nextFormStepUrl);
            }
        },
    },
    beforeMount() {
        this.$on('validChange', (input) => {
            if (input.$vnode.key === undefined) return;
            this.$store.commit('setValid', {
                pageIndex: this.page.index,
                questionIndex: input.$vnode.key.split('_')[0],
                isValid: input.isValid,
            });
        });
    },
};
</script>

<style lang="scss" scoped>
    @import "@/style_variables/style_variables.scss";
    main {
        width: 100%;
        min-height: calc(100vh - 68px);
        max-width: 792px;
        background-color: $white;
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        box-shadow: 0 10px 20px 0 rgba(30, 30, 30, 0.05);
        animation-name: loadZoomIn;
        animation-duration: 0.4s;

        @media (max-width: 769px) {
            margin: -24px auto 0px auto;
        }
        @media (min-width: 768px) {
            margin: -72px auto 0px auto;
        }
    }

    @keyframes loadZoomIn {
        from {
            opacity: 0;
            transform: scale(0.98);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    .form {
        width: 100%;
        padding: 24px 8px 0px;
        display: grid;
        grid-template-columns: 100%;
        grid-gap: 24px;
        grid-auto-rows: min-content;
        @media (max-width: 769px) {
            padding: 24px 8px;
        }
        @media (min-width: 768px) {
            padding: 24px 48px;
        }
    }

    .form__submit_holder {
        width: 100%;
        max-width: $imput_max_width;
        display: inline-flex;
        justify-content: space-between;
        transition: all 200ms ease-out;
        .submit {
            width: 100%;
            height: 48px;
        }
        .submit--anonymously {
            margin-right: 1em;
        }
    }
    .form__submit_holder--hidden {
        pointer-events: none;
        opacity: 0;
    }
    .divider {
        border: 0;
        border-top: 1px solid $grey_athens;
        @media (max-width: 769px) {
            width: calc(100% + 16px);
            margin: 0 0 0 -8px;
        }
        @media (min-width: 768px) {
            width: calc(100% + 96px);
            margin: 0 0 0 -48px;
        }
    }
</style>
